import { useCallback } from 'react';
import { useNavigate } from 'react-router';

/**
 * Returns the current history stack index from React Router's history state.
 * Returns 0 if the state is not available (e.g., direct URL access).
 */
export const getHistoryIndex = (): number => {
  return window.history.state?.idx ?? 0;
};

/**
 * Determines if there's enough history to navigate back safely.
 *
 * @param minHistoryEntries - Minimum number of history entries required to navigate back.
 *   - Use 1 for simple back buttons (need at least 1 previous entry)
 *   - Use 2 for drawers that add hash/params (drawer open adds 1 entry, so need 2 to go back properly)
 */
export const canNavigateBack = (minHistoryEntries: number = 1): boolean => {
  return getHistoryIndex() >= minHistoryEntries;
};

type SmartBackOptions = {
  /**
   * Fallback path when there's not enough history to navigate back.
   * @default '/'
   */
  fallbackPath?: string;

  /**
   * Minimum number of history entries required to use navigate(-1).
   * - 1: Standard back button (user came from another page)
   * - 2: Drawer/modal that adds URL state (hash or search param)
   * @default 1
   */
  minHistoryEntries?: number;
};

/**
 * Hook for smart back navigation that handles edge cases like:
 * - Direct URL access (no history to go back to)
 * - Proper fallback navigation when history is insufficient
 *
 * @example
 * // Simple back button - needs at least 1 history entry
 * const goBack = useSmartBack({ fallbackPath: '/' });
 *
 * @example
 * // Drawer close - needs at least 2 entries (drawer open added 1)
 * const goBack = useSmartBack({ fallbackPath: '/users', minHistoryEntries: 2 });
 */
export const useSmartBack = (options: SmartBackOptions = {}) => {
  const { fallbackPath = '/', minHistoryEntries = 1 } = options;
  const navigate = useNavigate();

  return useCallback(() => {
    if (canNavigateBack(minHistoryEntries)) {
      navigate(-1);
    } else {
      // Use replace to avoid creating navigation loops when this is used in redirects
      // (e.g., redirecting away from a page the user shouldn't be on)
      navigate(fallbackPath, { replace: true });
    }
  }, [navigate, fallbackPath, minHistoryEntries]);
};
